import { ESTIMATE_FILTER_LABEL, PHONE_MSG, estimateFilterOf, type EstimateFilter, type EstimateList, type EstimateSummary } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useTrashFlash } from "../estimates/Trash.jsx";
import { api, messageOf } from "../../api.js";
import { usePageName } from "../../components/BackButton.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { useModule } from "../../core/slots.jsx";
import { t } from "../../labels.js";
import { yen } from "../../lib/num.js";
import { PhIcon } from "./icons.jsx";
import { kickPhotoQueue } from "./photoQueue.js";

const FILTERS: EstimateFilter[] = ["all", "draft", "waiting", "ordered", "billed", "lost"];
/** スマホのタブの名前（#187 から PC と同じ言葉。ESTIMATE_FILTER_LABEL と同じ） */
const label = (k: EstimateFilter) => (k === "ordered" ? "受注" : ESTIMATE_FILTER_LABEL[k]);
const STATE_LABEL: Record<Exclude<EstimateFilter, "all">, string> = { draft: "下書き", waiting: "未受注", ordered: "受注", billed: "請求済", lost: "失注" };
/** 'YYYY-MM-DD' → 9/27 */
const mdSlash = (iso: string) => (iso ? `${+iso.slice(5, 7)}/${+iso.slice(8, 10)}` : "");

/**
 * S1 見積の一覧（スマホ版、#117、Figma 38:107）。PC の一覧（Estimates.tsx）と同じ絞り込み（?f=）・同じ API に ?view=phone。
 *   題「見積書」＋探す・状態のタブ（3列×2段・件数つき）・カード（工事名・状態・顧客・日付・行 n・写真 n・税込）・下に固定の「＋ 新しい見積」
 */
export function PhoneEstimatesPage() {
  usePageName(PHONE_MSG.title);
  useTrashFlash(); // #200：削除して戻った直後の「ゴミ箱に移しました」＋「元に戻す」
  const [sp, setSp] = useSearchParams();
  const f: EstimateFilter = (FILTERS as string[]).includes(sp.get("f") ?? "") ? (sp.get("f") as EstimateFilter) : "all";
  // 顧客の詳細から来たら、その顧客の見積だけ（PC の一覧と同じ ?client=）
  const clientId = sp.get("client") ?? "";
  const setF = (k: EstimateFilter) => setSp({ ...(k === "all" ? {} : { f: k }), ...(clientId ? { client: clientId } : {}) }, { replace: true });
  const list = useQuery({ queryKey: ["estimates", "phone"], queryFn: () => api<EstimateList>("GET", "/estimates?view=phone") });
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const qRef = useRef<HTMLInputElement>(null);
  useEffect(() => void kickPhotoQueue(), []);
  useEffect(() => {
    if (searching) qRef.current?.focus();
  }, [searching]);

  const words = q.trim().toLowerCase();
  const all = (list.data?.items ?? []).filter((x) => (!clientId || x.clientId === clientId) && (!words || `${x.subject} ${x.clientFullName ?? x.clientName} ${x.groupName ?? ""}`.toLowerCase().includes(words)));
  const count = (k: EstimateFilter) => (k === "all" ? all.length : all.filter((x) => estimateFilterOf(x) === k).length);
  const items = f === "all" ? all : all.filter((x) => estimateFilterOf(x) === f);

  return (
    <div className="ph-page ph-s1" data-testid="phone-estimates">
      <div className="ph-title">
        <h1>{PHONE_MSG.title}</h1>
        <button type="button" className="ph-iconbtn" aria-label={PHONE_MSG.search} aria-pressed={searching} onClick={() => setSearching((v) => !v)} data-testid="phone-search-toggle">
          <PhIcon k="search" />
        </button>
      </div>
      {searching ? (
        <div className="ph-search">
          <input ref={qRef} className="inp" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t(PHONE_MSG.searchPlaceholder)} aria-label={PHONE_MSG.search} data-testid="phone-search" />
        </div>
      ) : null}
      <div className="ph-tabs">
        <ListTabs id="pel" label="見積書を状態で絞る" tabs={FILTERS.map((k) => ({ key: k, label: label(k), count: list.data ? count(k) : "…" }))} value={f} onChange={setF} />
      </div>
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <ul className="ph-cards" {...listTabPanel("pel", f)}>
        {items.map((x) => (
          <li key={x.id}>
            <EstimateCard x={x} />
          </li>
        ))}
        {list.data && !items.length ? <li className="ph-empty">{PHONE_MSG.empty}</li> : null}
      </ul>
      <div className="ph-fixbar" data-notice-above="">
        <Link className="btn primary ph-main" to="/estimates/new" data-testid="phone-new-estimate">
          <PhIcon k="plus" />
          {PHONE_MSG.newEstimate}
        </Link>
      </div>
    </div>
  );
}

function EstimateCard({ x }: { x: EstimateSummary }) {
  const st = estimateFilterOf(x);
  const p = x.phone;
  const field = useModule("field"); // 写真の数は field の会社だけ（#142 段2）
  const meta = [mdSlash(x.issueDate), p ? PHONE_MSG.lines.replace("{n}", String(p.lineCount)) : null, p && field ? PHONE_MSG.photos.replace("{n}", String(p.photoCount)) : null].filter(Boolean).join("・");
  return (
    <Link className="ph-card" to={`/estimates/${x.id}`} data-testid="phone-estimate-card">
      <span className="r1">
        <b className="subj">{x.subject || t("（{subject}はまだ）")}</b>
        <span className={"chip" + (st === "ordered" ? " ai" : "")}>{STATE_LABEL[st]}</span>
      </span>
      {/* #302：スマホのカードは今までどおり正式名称（一覧の PC の表だけ略称＝API の clientName） */}
      <span className="cl">{x.clientFullName ?? x.clientName}</span>
      <span className="r3">
        <span className="meta">{meta}</span>
        <b className="num amt">{x.total ? `${yen(x.total)}円` : "—"}</b>
      </span>
    </Link>
  );
}
